import { Controller } from "@hotwired/stimulus";
import {Confirm, CONFIRM_DECLINED_EVENT} from "../utils/confirm.js"
import {isTurboRenderInProgress} from "../turbo-render-state.ts"

// Fired by Turbo/idiomorph on every element it reconciles during a morph refresh (see
// branching-select-controller.ts / searchable-select-controller.ts / dynamicTableController.ts for
// the same pattern). The form element survives a same-path replace visit, so connect() never
// re-runs; the baseline captured at connect() would otherwise go stale forever.
const MORPH_ELEMENT_EVENT = "turbo:morph-element";

interface StateBeforeSubmit {
   isDirty: boolean;
   buttonsDisabled: Map<HTMLButtonElement, boolean>;
}

export default class extends Controller {
   static override values = {
      disableOnSubmit: {type: Boolean, default: true},
      enableOnInputChange: {type: Boolean, default: false},
      confirmBeforePageLeave: {type: Boolean, default: true},
      autoSubmit: {type: Boolean, default: false}
   }

   declare element: HTMLFormElement;
   declare disableOnSubmitValue: boolean;
   declare enableOnInputChangeValue: boolean;
   declare confirmBeforePageLeaveValue: boolean;
   declare autoSubmitValue: boolean;
   
   isDirty: boolean = false;
   originalFormData: FormData = new FormData();
   guardedSelectPreviousValues: Map<HTMLSelectElement, string> = new Map();
   onPageNavigationEventHandler: (event: Event) => void = () => {};
   onFormSubmitEventHandler: (event: Event) => void = () => {};
   onConfirmDeclinedEventHandler: () => void = () => {};
   stateBeforeSubmit: StateBeforeSubmit | null = null;
   onChildInputEventHandler: (event: Event) => void = () => {};
   onChildChangeEventHandler: (event: Event) => void = () => {};
   onMorphElementEventHandler: (event: Event) => void = () => {};

   // Auto-submitting forms save on every change, so there is nothing unsaved to warn about when
   // leaving the page: autoSubmit implies the page-leave confirm is off. An explicit
   // data-form-confirm-before-page-leave-value attribute still wins in both directions.
   // Attribute presence is read from this.data directly: Stimulus's hasConfirmBeforePageLeaveValue
   // is always true for a value with a declared default, so it cannot tell explicit from default.
   get confirmsBeforePageLeave(): boolean {
      if (this.data.has("confirm-before-page-leave-value")) {
         return this.confirmBeforePageLeaveValue;
      }

      return !this.autoSubmitValue;
   }

   get submitButtons() {
      const buttons = [];
      buttons.push(...this.element.querySelectorAll("button[type=submit], input[type=submit]"));

      // Not `this.element.id`: a control named `id` replaces that property with the element.
      const formId = this.element.getAttribute("id");
      if (formId) {
         buttons.push(...document.querySelectorAll(`button[type=submit][form=${CSS.escape(formId)}]`));
      }

      return buttons;
   }

   override initialize() {
      // These are needed to bind the event handlers to the class instance.
      // If you don't do it like this you either don't have access to 'this' in your methods, or you can't remove the event listeners.
      // For more information on 'this' in event handlers, see: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#the_value_of_this_within_the_handler
      this.onPageNavigationEventHandler = this.onPageNavigation.bind(this);
      this.onFormSubmitEventHandler = this.onFormSubmit.bind(this);
      this.onConfirmDeclinedEventHandler = this.onConfirmDeclined.bind(this);
      this.onChildInputEventHandler = this.onChildInput.bind(this);
      this.onChildChangeEventHandler = this.onChildChange.bind(this);
      this.onMorphElementEventHandler = this.onMorphElement.bind(this);
   }

   override connect() {
      if (this.element.tagName !== "FORM") {
         console.error("The form controller should be added to the <form> element. It is set to: ", this.element.tagName);
         return;
      }

      window.addEventListener("beforeunload", this.onPageNavigationEventHandler);
      window.addEventListener("turbo:before-visit", this.onPageNavigationEventHandler);
      this.element.addEventListener("submit", this.onFormSubmitEventHandler);
      this.element.addEventListener(CONFIRM_DECLINED_EVENT, this.onConfirmDeclinedEventHandler);

      // Delegated on the document: a morph keeps this form without re-running connect(), so controls
      // rendered later must still reach this controller.
      document.addEventListener("input", this.onChildInputEventHandler);
      document.addEventListener("change", this.onChildChangeEventHandler);
      this.element.addEventListener(MORPH_ELEMENT_EVENT, this.onMorphElementEventHandler);

      this.ensureAutoSubmitReplaceAction();
      this.captureBaseline();
   }

   override disconnect() {
      window.removeEventListener("beforeunload", this.onPageNavigationEventHandler);
      window.removeEventListener("turbo:before-visit", this.onPageNavigationEventHandler);
      this.element.removeEventListener("submit", this.onFormSubmitEventHandler);
      this.element.removeEventListener(CONFIRM_DECLINED_EVENT, this.onConfirmDeclinedEventHandler);
      this.stateBeforeSubmit = null;
      document.removeEventListener("input", this.onChildInputEventHandler);
      document.removeEventListener("change", this.onChildChangeEventHandler);
      this.element.removeEventListener(MORPH_ELEMENT_EVENT, this.onMorphElementEventHandler);
   }

   // Re-seeds originalFormData and the guarded-select baseline from what the morph just rendered,
   // as if connect() had just run. Without this, a form that lives through several in-place
   // refreshes keeps comparing against the very first page load: reset() would restore stale
   // values, and a guarded select's cancel-revert would jump back further than the last render.
   private captureBaseline(): void {
      this.originalFormData = new FormData(this.element);

      this.guardedSelectPreviousValues.clear();
      for (const select of this.element.querySelectorAll("select[data-confirm-value]")) {
         this.guardedSelectPreviousValues.set(select as HTMLSelectElement, (select as HTMLSelectElement).value);
      }
   }

   // A person's action never fires this event, so a morph must never leave this controller
   // thinking there are unsaved changes: re-baseline and clear the dirty flag, matching a fresh
   // connect().
   onMorphElement(event: Event) {
      if (event.target !== this.element) {
         return;
      }

      // The server never renders the client-only data-turbo-action, so a morph strips it.
      this.ensureAutoSubmitReplaceAction();
      this.captureBaseline();
      this.isDirty = false;
   }

   // Ownership mirrors what `this.element.elements` already gave us at connect time: a listed
   // form-associated control's native `.form` property resolves to this form both when it is a
   // descendant of the <form> tag and when it lives elsewhere and points at it with a
   // `form="..."` attribute. Duck-typed on the `form` property rather than a list of tag-name
   // instanceof checks so any current or future listed form-associated element is covered.
   private ownedByThisForm(target: EventTarget | null): boolean {
      if (!(target instanceof Element) || !("form" in target)) {
         return false;
      }

      return (target as unknown as { form: HTMLFormElement | null }).form === this.element;
   }

   onPageNavigation(event: Event) {
      if (this.confirmsBeforePageLeave && this.isDirty) {
         event.preventDefault(); // Prevent the page from navigating away

         // A full page unload (reload, typed address, closed tab) has no URL to visit later and
         // cannot wait for a dialog; the cancelled beforeunload makes the browser ask instead.
         if (!(event instanceof CustomEvent)) {
            return;
         }

         const url = event.detail.url;
         // Rendered by the <unsaved-changes-dialog /> tag (UnsavedChangesDialogComponent), which fixes this same id.
         const dialogId = "unsaved-changes-dialog";

         // Without the dialog, a full page load lets the beforeunload guard raise the browser's own prompt.
         if (!document.getElementById(dialogId)) {
            window.location.assign(url);
            return;
         }

         Confirm.askConfirmation({
            dialogId: dialogId
         }).then(
            (doSubmit) => { // Confirm or Deny (user clicked 'save changes' or 'do not save changes')
               if (doSubmit) {
                  // This is needed because we want to wait until the submit is done before navigating to the new page.
                  // So, we register to the submit event once, and do the navigation when it fires, then submit the form.
                  this.element.addEventListener("turbo:submit-end", () => Turbo.visit(url), {once: true});
                  this.submit();
               } else {
                  this.reset();
                  Turbo.visit(url);
               }
            },
            () => { // Cancel (user clicked 'cancel')
               // Do nothing. Dialog closes without page navigation.
            }
         );
      }
   }

   // Turbo asks a data-turbo-confirm dialog only after this listener has run, and says nothing when
   // it is declined.
   onFormSubmit() {
      this.stateBeforeSubmit = {
         isDirty: this.isDirty,
         buttonsDisabled: new Map(this.submitButtons.map(button => [button, button.disabled]))
      };

      this.isDirty = false;

      if (this.disableOnSubmitValue) {
         this.disable();
      }
   }

   onConfirmDeclined() {
      const state = this.stateBeforeSubmit;
      this.stateBeforeSubmit = null;

      if (!state) {
         return;
      }

      this.isDirty = state.isDirty;

      for (const [button, disabled] of state.buttonsDisabled) {
         button.disabled = disabled;
      }
   }

   onChildInput(event: Event) {
      if (!this.ownedByThisForm(event.target)) {
         return;
      }

      this.isDirty = true;

      if (this.enableOnInputChangeValue) {
         this.enable();
      }
   }

   onChildChange(event: Event) {
      const target = event.target;

      // The browser fires `change` on a focused, edited control when a Turbo render removes it
      // from the document, or while a render is still in progress. That is not a user action:
      // ignore it entirely, including for the dirty flag and enable-on-input-change behaviour.
      if (!(target instanceof Node) || !target.isConnected || isTurboRenderInProgress()) {
         return;
      }

      if (!this.ownedByThisForm(target)) {
         return;
      }

      if (target instanceof HTMLSelectElement && target.hasAttribute("data-confirm-value")) {
         const confirmValue = target.getAttribute("data-confirm-value");

         if (target.value === confirmValue) {
            const previousValue = this.guardedSelectPreviousValues.get(target) ?? "";
            const message = target.getAttribute("data-confirm-message") ?? undefined;

            Confirm.askConfirmation({message}).then(
               (doConfirm: boolean) => {
                  if (doConfirm) {
                     this.guardedSelectPreviousValues.set(target, target.value);
                     this.applyChildChange(event);
                  } else {
                     target.value = previousValue;
                  }
               },
               () => {
                  target.value = previousValue;
               }
            );

            return;
         }

         this.guardedSelectPreviousValues.set(target, target.value);
      }

      this.applyChildChange(event);
   }

   applyChildChange(event: Event) {
      this.isDirty = true;

      if (this.enableOnInputChangeValue) {
         this.enable();
      }

      if (this.autoSubmitValue) {
         this.submit();
      }
   }

   submit() {
      this.ensureAutoSubmitReplaceAction();
      this.element.requestSubmit();
   }

   // Left in place: Turbo reads the attribute only after the fetch response returns.
   private ensureAutoSubmitReplaceAction(): void {
      if (this.autoSubmitValue && !this.element.hasAttribute("data-turbo-action")) {
         this.element.setAttribute("data-turbo-action", "replace");
      }
   }

   reset() {
      for (const [name, value] of this.originalFormData.entries()) {
         const inputElement: HTMLInputElement | null = this.element.querySelector(`[name="${name}"]`);
         if (inputElement) {
            if (inputElement.type === "checkbox" || inputElement.type === "radio") {
               inputElement.checked = value == true || value === "on" || value === "yes";
            } else {
               inputElement.value = value;
            }
         }
      }

      // The restore above rewrote guarded select values without change events, so the tracked
      // previous values would be stale for the next cancel-revert. Re-seed from what reset left.
      for (const select of this.guardedSelectPreviousValues.keys()) {
         this.guardedSelectPreviousValues.set(select, select.value);
      }

      this.isDirty = false;
   }

   disable() {
      this.submitButtons.forEach(button => {
         button.disabled = true;
      });
   }

   enable() {
      this.submitButtons.forEach(button => {
         button.disabled = false;
      });
   }
}